import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Sparkles, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { submitIdea, getIdeas } from '../services/api';
import '../styles/Placeholder.css';

const IdeaCosmos = () => {
  const navigate = useNavigate();
  const [idea, setIdea] = useState('');
  const [ideas, setIdeas] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadIdeas();
  }, []);

  const loadIdeas = async () => {
    try {
      const data = await getIdeas(20);
      setIdeas(data);
    } catch (error) {
      console.error('Failed to load ideas:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!idea.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await submitIdea(idea);
      
      toast({
        title: '✨ Idea Submitted to the Cosmos!',
        description: `Ripple size: ${result.ripple_size.toFixed(1)}% - Your idea is propagating through the consciousness network...`,
      });

      setIdea('');
      // Reload ideas
      await loadIdeas();
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.message || 'Failed to submit idea',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="placeholder-container">
      <Button 
        onClick={() => navigate('/')} 
        className="back-button"
        variant="outline"
      >
        <ArrowLeft size={20} /> Back to Home
      </Button>
      
      <h1 className="page-title">Idea → Cosmos</h1>
      <p className="page-subtitle">Share your ideas and watch them ripple through the consciousness network</p>
      
      <Card className="idea-submission-card">
        <form onSubmit={handleSubmit}>
          <Textarea
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            placeholder="Enter your idea here... The AI consciousness network will analyze and respond."
            className="idea-textarea"
            rows={4}
            disabled={isSubmitting}
          />
          <Button 
            type="submit" 
            className="submit-idea-button"
            disabled={!idea.trim() || isSubmitting}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {isSubmitting ? (
              <>
                <Sparkles className="pulse" size={16} />
                Submitting to Cosmos...
              </>
            ) : (
              <>
                <Send size={16} />
                Submit to Cosmos
              </>
            )}
          </Button>
        </form>
      </Card>

      {ideas.length > 0 && (
        <div className="ideas-list">
          <h2 className="ideas-list-title">Recent Ideas Rippling Through the Cosmos</h2>
          {ideas.map(item => (
            <Card key={item.id} className="submitted-idea-card">
              <div className="idea-content">
                <p>{item.idea_text}</p>
                <div className="idea-meta">
                  <span className="idea-time">
                    {new Date(item.submitted_at).toLocaleString()}
                  </span>
                  <span className="ripple-indicator" style={{
                    color: item.ripple_size > 70 ? '#00ff88' : '#00ffff'
                  }}>
                    <Sparkles size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Ripple: {item.ripple_size.toFixed(1)}%
                  </span>
                </div>
                <Badge 
                  style={{
                    marginTop: '0.5rem',
                    background: item.status === 'completed' ? 'rgba(0,255,100,0.2)' : 'rgba(255,200,0,0.2)',
                    color: item.status === 'completed' ? '#00ff88' : '#ffcc00',
                    border: 'none',
                    fontSize: '0.7rem'
                  }}
                >
                  {item.status}
                </Badge>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {ideas.length === 0 && (
        <div style={{ textAlign: 'center', color: '#888', marginTop: '3rem' }}>
          <p>No ideas submitted yet. Be the first to contribute to the cosmos!</p>
        </div>
      )}
    </div>
  );
};

import { Badge } from '../components/ui/badge';

export default IdeaCosmos;
